import {
  QueryEvent,
  QueryStatus,
} from '@restore/queries/queries.types';
import { UserRoles } from '@dynamika-shared/types/auth';

export type Transition = {
  from: QueryStatus;
  to?: QueryStatus;
  roles: UserRoles[];
  requiresVerification?: boolean;
};

export type QueryCapability = {
  event: QueryEvent;
  nextStatus: QueryStatus;
  requiresVerification?: boolean;
  internal: boolean;
};

export type QueryCapabilities = {
  canSendMessage: boolean;
  availableEvents: QueryCapability[];
  canDo: (event: QueryEvent) => boolean;
  getEventMeta: (event: QueryEvent) => QueryCapability | undefined;
};

export const QUERY_WORKFLOW: Record<QueryEvent, Transition[]> = {
  [QueryEvent.SUBMIT]: [
    {
      from: QueryStatus.CREATED,
      to: QueryStatus.OPEN_SITE,
      roles: [UserRoles.SITE],
    },
  ],
  [QueryEvent.SEND_MESSAGE]: [
    {
      from: QueryStatus.OPEN_SITE,
      roles: [UserRoles.SITE, UserRoles.IMAGING_SPECIALIST],
    },
    {
      from: QueryStatus.OPEN_IAG,
      roles: [UserRoles.IMAGING_SPECIALIST],
    },
  ],
  [QueryEvent.RESPOND]: [
    {
      from: QueryStatus.OPEN_SITE,
      to: QueryStatus.OPEN_IAG,
      roles: [UserRoles.SITE],
      requiresVerification: true,
    },
  ],
  [QueryEvent.CLOSE]: [
    {
      from: QueryStatus.OPEN_IAG,
      to: QueryStatus.CLOSED,
      roles: [UserRoles.IMAGING_SPECIALIST],
      requiresVerification: true,
    },
    {
      from: QueryStatus.OPEN_SITE,
      to: QueryStatus.CLOSED,
      roles: [UserRoles.IMAGING_SPECIALIST],
      requiresVerification: true,
    },
  ],
  [QueryEvent.REOPEN]: [
    {
      from: QueryStatus.CLOSED,
      to: QueryStatus.OPEN_SITE,
      roles: [UserRoles.IMAGING_SPECIALIST],
    },
  ],
  [QueryEvent.NEEDS_REWORK]: [
    {
      from: QueryStatus.OPEN_IAG,
      to: QueryStatus.OPEN_SITE,
      roles: [UserRoles.IMAGING_SPECIALIST],
    },
  ],
  [QueryEvent.ESCALATE]: [],
};

export function getQueryCapabilities(
  status: QueryStatus,
  role: UserRoles
): QueryCapabilities {
  const availableEvents: QueryCapability[] = (
    Object.keys(QUERY_WORKFLOW) as QueryEvent[]
  )
    .map(event => {
      const transition = QUERY_WORKFLOW[event].find(
        t => t.from === status && t.roles.includes(role)
      );
      if (!transition) return null;

      return {
        event,
        nextStatus: transition.to ?? status,
        requiresVerification: !!transition.requiresVerification,
        internal: !transition.to,
      };
    })
    .filter(Boolean) as QueryCapability[];

  return {
    canSendMessage: availableEvents.some(
      e => e.event === QueryEvent.SEND_MESSAGE
    ),
    availableEvents,
    canDo: (event: QueryEvent) => availableEvents.some(e => e.event === event),
    getEventMeta: (event: QueryEvent) =>
      availableEvents.find(e => e.event === event),
  };
}
